/** @jsxImportSource @emotion/react */

import styled from '@emotion/styled'
import CheckboxComponent from "components/atoms/Checkbox";
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Select from 'react-select'

import { Order } from 'store/types'
import {
  useCompleteHstOrderMutation,
  useCancelWesperOrderMutation,
  useCreateWesperOrderMutation,
  useSetInterpretingPhysicianMutation,
  useSetLabReportPendingMutation,
  useUpdateHstStatusMutation,
  useUploadHstReportMutation,
} from 'store/services/labOrder'
import { useLazyGetLabPhysiciansQuery } from 'store/services/labPhysician'
import Upload from 'components/atoms/Upload'
import Toast from 'components/atoms/Toast'
import Dropdown from 'react-bootstrap/Dropdown'
import DateTimePicker from '../../../atoms/DateTimePicker'
import Input from '../../../atoms/Input'
import { confirm } from '../../../molecules/Confirmation'
import { canRetestWesperOrder } from 'helpers/orderStateHelpers'
import { hstStatusOptions } from './DeviceShipping'
import { TimeWrapper } from './FollowUpForm'
import { ReportUploadModal } from './ReportUploadModal'

type PhysicianOption = { value: number; label: string }

const StyledDropdownItem = styled(Dropdown.Item)`
  padding: 0.55rem 1rem;
`

interface IProps {
  order: Order
}

export const PatientTesting = ({ order }: IProps) => {
  const [hstStatus, setHstStatus] = useState({ value: 'S002', label: 'Device Shipped' })
  const [updateStatus] = useUpdateHstStatusMutation()
  const [createWesperOrder, { isLoading: isCreatingWesperOrder }] = useCreateWesperOrderMutation()
  const [cancelWesperOrder, { isLoading: isCancellingWesperOrder }] = useCancelWesperOrderMutation()
  const [setInterpretingPhysician] = useSetInterpretingPhysicianMutation()
  const [getInterpretingPhysicians, { data: interpretingPhysiciansData, isLoading: isLoadingInterpretingPhysicians }] =
    useLazyGetLabPhysiciansQuery()
  const [uploadReport] = useUploadHstReportMutation()
  const [completeHstOrder] = useCompleteHstOrderMutation()
  const [setReportPending] = useSetLabReportPendingMutation()
  const [uploadType, setUploadType] = useState('')
  const [dateOfService, setDateOfService] = useState<string | undefined>(undefined)
  const [ahi, setAhi] = useState<string | undefined>(undefined)
  const uploadRef = useRef<HTMLInputElement>()
  const [hstModalOpen, setHstModalOpen] = useState(false)
  const [isReportPending, setIsReportPending] = useState<boolean>(!!order.hst?.reportPending)
  const hasWesperDevice = order?.device?.model?.toLowerCase() === 'wesper'
  const isWesperOrder =
    order?.testType === 'HST' &&
    (hasWesperDevice || !!order?.wesperOrderCreatedAt || order?.neededDeviceType === 'Type 3: Wesper')
  // A Wesper order already exists, but staff can place a new (re-test) order on the same TS
  // order once the device has made its way back to Wesper — mirrors the backend guard in
  // create-wesper-order.handler.ts:74 (DEVICE_DELIVERED_BACK / DEVICE_RETURNED).
  const canRetest = canRetestWesperOrder(order)
  // Staff cancelled in Wesper's portal and need the TS order unlocked so a different fulfillment
  // can be chosen. Refused once the kit is moving or results exist — that is a return, not a
  // cancellation. Mirrors the backend guards in cancel-wesper-order.handler.ts.
  const canCancelWesper =
    !!order?.wesperOrderCreatedAt &&
    !order?.wesperInfo?.trackingCode &&
    (order?.wesperInfo?.sessions?.length ?? 0) === 0
  const canCreateDispense = hasWesperDevice && (!order?.wesperOrderCreatedAt || canRetest)
  const canCreateDropship =
    order?.testType === 'HST' && !order?.deviceId && (!order?.wesperOrderCreatedAt || canRetest)

  const [wesperReviewOpen, setWesperReviewOpen] = useState(false)
  const pendingReview = order?.wesperInfo?.pendingReview
  // Scope to the interpretation the pending report belongs to — NOT simply the highest seq.
  // While a re-interpretation is mid-arrival the newest set is incomplete and `pendingReview`
  // still points at the previous, complete one; keying off the highest seq would show that
  // older PDF beside the new set's partial AHI. `pendingReview` is the authoritative pointer
  // to what staff are attaching, so its session's set is the one to prefill from. This also
  // holds when the grace clock forces review on an incomplete set.
  const wesperSessions = order?.wesperInfo?.sessions ?? []
  const pendingSessionId = order?.wesperInfo?.pendingReview?.sessionId
  // A matched session with no interpretationSeq (stored before this deploy) must select seq 0 —
  // not fall through to the highest-seq reduce, which would pull AHI/dates from a newer,
  // unrelated set while the modal shows this older night's PDF. Only "no matching session at
  // all" (pendingReview pointing at nothing we hold, which shouldn't normally happen) falls
  // back to that reduce. Seq 0 then selects the whole legacy pool via the
  // `(s.interpretationSeq ?? 0) === currentSeq` filter below, restoring the pre-existing
  // max-AHI-across-all-legacy-nights behavior for those orders.
  const matchedSession = wesperSessions.find((s) => s.sessionId === pendingSessionId)
  const currentSeq = matchedSession
    ? matchedSession.interpretationSeq ?? 0
    : wesperSessions
      .filter((s) => s.status !== 'Draft')
      .reduce((max, s) => Math.max(max, s.interpretationSeq ?? 0), 0)
  const currentSet = wesperSessions.filter(
    (s) => s.status !== 'Draft' && (s.interpretationSeq ?? 0) === currentSeq,
  )
  const withAhi = currentSet.filter((s) => s.ahi4 !== null && s.ahi4 !== undefined)
  const bestWesperSession = withAhi.length
    ? withAhi.reduce((best, s) =>
      Number(s.ahi4) > Number(best.ahi4)
        || (Number(s.ahi4) === Number(best.ahi4) && (s.sessionNumber ?? 1) < (best.sessionNumber ?? 1))
        ? s
        : best)
    : currentSet[0]
  // Best night first (its date carries the AHI), then the rest in night order — dates and
  // testing times are built from this one list so they stay aligned row for row.
  const prefillSessions = [
    bestWesperSession,
    ...currentSet
      .filter((s) => s !== bestWesperSession)
      .sort((a, b) => (a.sessionNumber ?? 1) - (b.sessionNumber ?? 1)),
  ]
    .filter((s): s is NonNullable<typeof s> => !!s?.sessionDate)
    .slice(0, 3)
  const wesperAttach = pendingReview
    ? {
        sessionId: pendingReview.sessionId,
        fileUrl: order?.files?.[pendingReview.fileKey]?.previewUrl ?? '',
        prefill: {
          datesOfService: prefillSessions.map((s) => s.sessionDate),
          testingTimes: prefillSessions.map((s) =>
            s.recordingTimeSeconds != null ? Math.round(s.recordingTimeSeconds / 60) : null,
          ),
          ahi: bestWesperSession?.ahi4 != null ? String(bestWesperSession.ahi4) : '',
          interpretingDate: bestWesperSession?.reviewDate ?? null,
          interpretingPhysicianId: order?.interpretingPhysicianId ?? null,
          interpretingProviderName: bestWesperSession?.interpretingProviderName ?? null,
        },
      }
    : undefined

  useEffect(() => {
    if (order?.testType === 'HST') {
      getInterpretingPhysicians({ physicianType: 'INTERPRETING', noOffice: 'true', hasWesperGroup: 'true' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.testType])

  const interpretingPhysiciansOptions: PhysicianOption[] =
    interpretingPhysiciansData?.map((physician) => {
      const fullName = [physician.firstName, physician.middleName, physician.lastName]
        .filter(Boolean)
        .join(' ')
      return {
        // physician.id is typed `number` but physicians.id is a bigint column, which
        // the pg driver returns as a string at runtime — normalize here so the Select's
        // `value` lookup below (strict equality against order.interpretingPhysicianId,
        // a real number from an `int` column) actually matches. Same pattern as
        // FollowUpCareReports.tsx.
        value: Number(physician.id),
        label: fullName || String(physician.id),
      }
    }) || []

  const toggleReportPending = async (pending: boolean) => {
    try {
      setIsReportPending(pending)
      await setReportPending({ orderId: Number(order.id), pending }).unwrap()
      Toast({
        type: 'success',
        label: pending ? 'Marked order as report pending' : 'Returned to patient testing',
      })
    } catch (err) {
      setIsReportPending(!pending)
      Toast({ type: 'error', label: 'Could not update report status' })
    }
  }

  useEffect(() => {
    setIsReportPending(!!order.hst?.reportPending)
  }, [order])

  const handleUpload = async (acceptedFiles: File[]) => {
    const [file] = acceptedFiles
    if (!file?.name) return
    const formData = new FormData()
    formData.append('report', file)
    formData.append('orderId', String(order.id))
    formData.append('reportType', uploadType)
    formData.append('dateOfService', dateOfService)

    if (ahi) {
      formData.append('ahi', ahi)
    }

    uploadReport({ orderId: Number(order.id), formData }).then(() => {
      Toast({ type: 'success', label: `Report uploaded!` })
    })
  }

  const startUpload = (type: string) => () => {
    setUploadType(type)
    uploadRef?.current?.click()
  }

  const handleOpenHstModal = (reportType: string) => {
    setUploadType(reportType)
    setHstModalOpen(true)
  }

  const renderCreateWesperButton = (fulfillment: 'DISPENSE' | 'DROPSHIP', label: string) => {
    const missingPhysician = !order?.interpretingPhysicianId
    const button = (
      <Button
        variant="outline-primary"
        disabled={missingPhysician || isCreatingWesperOrder}
        onClick={() => onCreateWesperOrder(fulfillment)}
        className="text-nowrap"
        style={missingPhysician ? { pointerEvents: 'none' } : undefined}>
        {isCreatingWesperOrder ? 'Creating Wesper Order...' : label}
      </Button>
    )

    if (!missingPhysician) {
      return button
    }

    return (
      <OverlayTrigger
        placement="bottom"
        overlay={<Tooltip>Select an interpreting physician first</Tooltip>}>
        <span className="d-inline-block" tabIndex={0}>
          {button}
        </span>
      </OverlayTrigger>
    )
  }

  const onCancelWesperOrder = async () => {
    if (!order?.id) return

    confirm('', {
      title: 'Cancel Wesper Order',
      description:
        'This unlocks the TestSmarter order so a device can be assigned or a different fulfillment chosen. ' +
        'It does NOT cancel anything at Wesper — cancel the order in the Wesper portal separately.',
      yesAction: 'Cancel Wesper Order',
      noAction: 'Back',
    })
      .then(async () => {
        try {
          const result = await cancelWesperOrder({ orderId: Number(order.id) }).unwrap()
          if (result?.success !== true) {
            Toast({
              type: 'error',
              label: `Wesper cancellation failed: ${result?.error || 'Unable to cancel Wesper order.'}`,
            })
            return
          }
          Toast({ type: 'success', label: 'Wesper order cancelled. Remember to cancel it in the Wesper portal.' })
        } catch (err) {
          const message = (err as { data?: { message?: string } })?.data?.message

          Toast({ type: 'error', label: message || 'Unable to cancel Wesper order.' })
        }
      })
      .catch(() => {})
  }

  const onCreateWesperOrder = async (fulfillment: 'DISPENSE' | 'DROPSHIP') => {
    // Once a Wesper order already exists, only a re-test placement (device returned) may proceed.
    if (!order?.id || (order?.wesperOrderCreatedAt && !canRetest)) return

    const title = canRetest
      ? fulfillment === 'DROPSHIP'
        ? 'Create Wesper Re-test Dropship Order'
        : 'Create Wesper Re-test Order'
      : fulfillment === 'DROPSHIP'
        ? 'Create Wesper Dropship Order'
        : 'Create Wesper Order'

    confirm('', {
      title,
      description: canRetest
        ? 'This will create a re-test Wesper order for this HST order.'
        : fulfillment === 'DROPSHIP'
          ? 'This will create a dropship Wesper order for this HST order.'
          : 'This will create the Wesper order for this HST order.',
      yesAction: title,
      noAction: 'Cancel',
    })
      .then(async () => {
        try {
          const result = await createWesperOrder({ orderId: Number(order.id), fulfillment }).unwrap()
          if (result?.success !== true) {
            Toast({ type: 'error', label: `Wesper order failed: ${result?.error || 'Unable to create Wesper order.'}` })
            return
          }
          Toast({ type: 'success', label: 'Wesper order created successfully.' })
        } catch (err) {
          Toast({ type: 'error', label: err?.data?.message || 'Unable to create Wesper order.' })
        }
      })
      .catch(() => {})
  }

  return (
    <div className="w-100 mt-2 pl-3">
      <Upload className="invisible" ref={uploadRef} handleUpload={handleUpload} invisible />
      <ReportUploadModal
        open={hstModalOpen}
        onClose={() => setHstModalOpen(false)}
        order={order}
        reportType={uploadType}
      />
      <ReportUploadModal
        open={wesperReviewOpen}
        onClose={() => setWesperReviewOpen(false)}
        order={order}
        reportType="HST"
        wesperAttach={wesperAttach}
      />
      <div
        className="row"
        css={{
          width: '100%',
        }}>
        <div
          className="p-0 d-flex align-items-center justify-content-between"
          css={{
            width: '100%',
          }}>
          <div
            className="d-flex align-items-end justify-content-between"
            css={{
              width: '100%',
            }}>
            <div className="d-flex align-items-center">
              <Select
                className="basic-single mb-2"
                classNamePrefix="select"
                isSearchable
                placeholder="Select status"
                name="hst_status"
                onChange={(item) => {
                  if (!item) {
                    return
                  }

                  setHstStatus(item)
                }}
                options={hstStatusOptions}
                value={hstStatus}
                css={{
                  width: 300,
                  marginBottom: '0 !important',
                }}
              />
              <Button
                variant="outline-primary"
                onClick={async () => {
                  await updateStatus({
                    orderId: Number(order.id),
                    status: hstStatus.value,
                  })
                }}
                style={{
                  marginLeft: 10,
                }}>
                Set shipping status
              </Button>
            </div>
            {pendingReview && (
              <div
                className="d-flex flex-column align-items-center justify-content-center mx-3"
                css={{ minWidth: 200 }}>
                <Button
                  variant="warning"
                  className="text-nowrap"
                  onClick={() => setWesperReviewOpen(true)}>
                  Attach Wesper Report
                </Button>
              </div>
            )}
            {(isWesperOrder || canCreateDropship) && (
              <div
                className="d-flex flex-column align-items-center justify-content-center mx-3"
                css={{ minWidth: 260 }}>
                {order?.wesperOrderCreatedAt && (
                  <div className="text-muted mb-1" css={{ fontSize: 12 }}>
                    Wesper created {moment(order.wesperOrderCreatedAt).format('lll')}
                    {order?.wesperInfo?.orderCode && ` · Code ${order.wesperInfo.orderCode}`}
                  </div>
                )}
                <Select
                  className="basic-single mb-2 w-100"
                  classNamePrefix="select"
                  isClearable
                  isSearchable
                  isDisabled={!!order?.wesperOrderCreatedAt && !canRetest}
                  isLoading={isLoadingInterpretingPhysicians}
                  name="interpretingPhysician"
                  placeholder="Interpreting physician..."
                  options={interpretingPhysiciansOptions}
                  value={
                    interpretingPhysiciansOptions.find(
                      (o) =>
                        order?.interpretingPhysicianId != null &&
                        Number(o.value) === Number(order.interpretingPhysicianId),
                    ) ?? null
                  }
                  onChange={(opt) =>
                    setInterpretingPhysician({
                      orderId: Number(order.id),
                      physicianId: opt ? Number(opt.value) : null,
                    })
                  }
                  css={{ width: '100%' }}
                />
                {order?.wesperOrderCreatedAt && !canRetest ? (
                  <>
                    <Button variant="outline-success" disabled className="text-nowrap">
                      Wesper Order Created
                    </Button>
                    {canCancelWesper && (
                      <Button
                        variant="outline-danger"
                        disabled={isCancellingWesperOrder}
                        onClick={onCancelWesperOrder}
                        className="text-nowrap ml-2">
                        {isCancellingWesperOrder ? 'Cancelling...' : 'Cancel Wesper Order'}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {canCreateDispense &&
                      renderCreateWesperButton(
                        'DISPENSE',
                        canRetest ? 'Create Wesper Re-test Order' : 'Create Wesper Order',
                      )}
                    {canCreateDropship &&
                      renderCreateWesperButton(
                        'DROPSHIP',
                        canRetest ? 'Create Wesper Re-test Dropship Order' : 'Create Wesper Dropship Order',
                      )}
                  </>
                )}
              </div>
            )}
            {order?.state?.HST !== 'DONE' && (
              <div className="d-flex align-items-end" css={{ minWidth: 500 }}>
                <div css={{ whiteSpace: 'nowrap', marginRight: 10, paddingBottom: 8 }}>
                  <CheckboxComponent
                    label={'Report Pending'}
                    value={isReportPending}
                    handleChange={(e) => toggleReportPending(e.target.checked)}
                  />
                </div>
                <TimeWrapper>
                  <label className="textGray" htmlFor="effectiveDate">
                    Date of Service
                    <span className="text-danger">*</span>
                  </label>
                  <DateTimePicker
                    value={dateOfService && moment(dateOfService, 'YYYY-MM-DD').toDate()}
                    handleChange={(date) => {
                      setDateOfService(moment(date).format('YYYY-MM-DD'))
                    }}
                    showTimeSelect={false}
                    dateFormat="MMMM d, yyyy"
                  />
                </TimeWrapper>
                <div className="col-3 textGray">
                  <label htmlFor="ahi">AHI</label>
                  <Input
                    type="number"
                    name="ahi"
                    minValue="0"
                    maxValue="300"
                    className="form-control"
                    placeholder="0-300"
                    value={ahi ?? ''}
                    handleChange={(e) => {
                      setAhi(Number(e.target.value) > 300 ? '300' : e.target.value)
                    }}
                  />
                </div>
                <DropdownButton
                  title="Upload Report "
                  className="ml-2"
                  onClick={(e) => e.stopPropagation()}
                  css={{ cursor: 'default' }}>
                  <StyledDropdownItem disabled={!dateOfService} onClick={startUpload('PSG')}>
                    PSG
                  </StyledDropdownItem>
                  <StyledDropdownItem disabled={!dateOfService} onClick={startUpload('TITRATION')}>
                    Titration
                  </StyledDropdownItem>
                  <StyledDropdownItem onClick={() => handleOpenHstModal('HST')}>
                    HST
                  </StyledDropdownItem>
                  <StyledDropdownItem onClick={() => handleOpenHstModal('FAILED_HST')}>
                    Failed HST
                  </StyledDropdownItem>
                </DropdownButton>
                {!!order.reportRenderRequests.length && (
                  <Button
                    variant="primary"
                    className="ml-2 text-nowrap"
                    onClick={async () => {
                      confirm('', {
                        title: 'Are you sure?',
                        description:
                          'This will mark the order as complete and start the fax process.',
                        yesAction: 'Yes, proceed',
                        noAction: 'Cancel',
                      })
                        .then(async () => {
                          try {
                            await completeHstOrder({ orderId: Number(order.id) })
                            Toast({ type: 'success', label: `Order completed` })
                          } catch (err) {
                            Toast({ type: 'error', label: `Error completing order.` })
                          }
                        })
                        .catch(() => {})
                    }}>
                    Complete Order
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!!order?.wesperInfo?.sessions?.length && (
        <div className="row mt-2">
          <div className="col-12">
            <div className="text-muted mb-1" css={{ fontSize: 12, fontWeight: 600 }}>
              Wesper Reports
            </div>
            <ul className="list-unstyled mb-0" css={{ fontSize: 13 }}>
              {order.wesperInfo.sessions.map((session, index) => {
                const previewUrl = order.files?.[session.fileKey]?.previewUrl

                return (
                  <li key={session.sessionId} className="d-flex align-items-center mb-1">
                    <span className="mr-2">
                      Night {session.sessionNumber ?? index + 1}
                      {' — '}
                      {session.sessionDate ? moment(session.sessionDate).format('ll') : 'Unknown date'}
                      {' — '}
                      {session.status}
                    </span>
                    {previewUrl && (
                      <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}
      {/* <div className="row">
        <div
          className="col-lg-12 p-3 mt-3 d-flex flex-column align-items-end"
          css={{ background: '#F5F5F5' }}>
          <div className="w-100 mb-3 ml-2" css={{ backgroundColor: 'white' }}>
            <DeviceShippingStepper
              steps={[
                {
                  id: 1,
                  label: '1',
                  title: 'Data uploaded for Night 1',
                  ...getStatus(DreamclearStatusCodes.S004),
                },
                {
                  id: 2,
                  label: '2',
                  title: 'Data uploaded for Night 2',
                  ...getStatus(DreamclearStatusCodes.S005),
                },
                {
                  id: 3,
                  label: '3',
                  title: 'Data uploaded to Ensodata for Night 1',
                  ...getStatus(DreamclearStatusCodes.S008),
                },
                {
                  id: 4,
                  label: '4',
                  title: 'Data uploaded to Ensodata for Night 2',
                  ...getStatus(DreamclearStatusCodes.S009),
                },
                {
                  id: 5,
                  label: '5',
                  title: 'Study is Scored',
                  ...getStatus([DreamclearStatusCodes.S010, DreamclearStatusCodes.S011]),
                },
                {
                  id: 6,
                  label: '6',
                  title: 'Report Downloaded from Ensodata',
                  ...getStatus(DreamclearStatusCodes.S013),
                },
                {
                  id: 7,
                  label: '7',
                  title: 'Interpretation Signed',
                  ...getStatus(DreamclearStatusCodes.S014),
                },
              ]}
            />
          </div>
        </div>
      </div>*/}
    </div>
  )
}
